import { useEffect, useRef } from 'react';
import type { Story, StoryFrame } from './api';

interface StoryFrameImageProps {
  story: Story;
  frame: StoryFrame;
  failed: boolean;
  onReady: () => void;
  onFailed: () => void;
}

/**
 * The artwork behind one frame.
 *
 * Split out for one reason: telling the player the image is ready is harder than
 * an `onLoad` handler, and getting it wrong freezes the whole viewer.
 *
 * The viewer preloads the next frame's image. By the time that frame becomes
 * current the browser already has it decoded, `img.complete` is true before
 * React attaches a listener, and **`onLoad` never fires** — so the readiness gate
 * never opens and the story stops dead. It fails on fast connections and repeat
 * visits, which is exactly why it survives manual testing.
 *
 * So readiness is asked for imperatively, via `decode()`, which resolves
 * immediately for an image that is already there.
 */
export function StoryFrameImage({ story, frame, failed, onReady, onFailed }: StoryFrameImageProps) {
  const imageRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const image = imageRef.current;

    if (!image || failed) {
      return;
    }

    let cancelled = false;

    image
      .decode()
      .then(() => {
        if (!cancelled) {
          onReady();
        }
      })
      .catch(() => {
        // decode() rejects both for a genuinely broken image and for one whose
        // src changed mid-flight. The cancelled flag separates the two.
        if (!cancelled) {
          onFailed();
        }
      });

    return () => {
      cancelled = true;
    };
  }, [failed, frame.id, onFailed, onReady]);

  if (failed) {
    // A missing image must not take the frame with it: the headline and the call
    // to action are what convert, and they are rendered by the viewer above this.
    return (
      <div
        className="absolute inset-0 size-full"
        style={{
          background: `radial-gradient(120% 90% at 50% 20%, ${story.brandColor}33, transparent 70%), #0a0a0a`,
        }}
        aria-hidden
      >
        {story.siteIconUrl ? (
          <img
            src={story.siteIconUrl}
            alt=""
            width={96}
            height={96}
            className="absolute left-1/2 top-[38%] size-24 -translate-x-1/2 rounded-full object-cover opacity-90"
          />
        ) : null}
      </div>
    );
  }

  return (
    <img
      ref={imageRef}
      key={frame.id}
      src={frame.mediaUrl}
      // Null alt means the artwork is decorative — the headline beside it already
      // carries the meaning and would otherwise be announced twice.
      alt={frame.altText ?? ''}
      onError={onFailed}
      className="absolute inset-0 size-full object-contain"
      draggable={false}
    />
  );
}
